import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SpaceFeatureIcon } from '../../services/sala.service';

const normalize = (value: string) => value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/**
 * Autoselect del catálogo de iconos de características (mismo patrón que
 * app-bank-select / app-category-autocomplete: se escribe para filtrar,
 * flechas + Enter o clic para elegir). Se elige un CONCEPTO con su texto
 * ("Internet / Wi-Fi"), no un dibujo suelto, para que cada icono signifique
 * siempre lo mismo. Muestra a lo sumo `limit` resultados agrupados por
 * categoría, así escala aunque el catálogo crezca.
 */
@Component({
  selector: 'app-feature-icon-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="fis">
      <div class="fis-control" [class.invalid]="invalid" [class.has-value]="!!selected">
        <span class="fis-current" aria-hidden="true"><i class="bi" [ngClass]="'bi-' + (selected?.id || 'search')"></i></span>
        <input placeholder="Buscar ícono…"
          #searchInput
          type="text"
          [id]="inputId"
          role="combobox"
          [attr.aria-expanded]="isOpen()"
          [attr.aria-controls]="inputId + '-list'"
          [attr.aria-activedescendant]="activeIndex() >= 0 ? inputId + '-opt-' + activeIndex() : null"
          [(ngModel)]="searchText"
          (focus)="open()"
          (blur)="onBlur()"
          (input)="onType()"
          (keydown)="onKeydown($event)"
          [placeholder]="selected ? selected.label : 'Busca: wifi, pantalla, café, parqueo…'"
          autocomplete="off"
          data-no-limit-hint
        >
        <button type="button" class="fis-clear" *ngIf="selected && !searchText" (mousedown)="$event.preventDefault(); focusSearch()" aria-label="Cambiar icono"><i class="bi bi-chevron-down"></i></button>
      </div>
      <div class="fis-panel" *ngIf="isOpen()" [id]="inputId + '-list'" role="listbox">
        <ng-container *ngFor="let group of groups(); trackBy: trackGroup">
          <p class="fis-group">{{ group.name }}</p>
          <button
            type="button"
            *ngFor="let item of group.items; trackBy: trackItem"
            class="fis-option"
            role="option"
            [id]="inputId + '-opt-' + item.index"
            [attr.aria-selected]="item.icon.id === selected?.id"
            [class.active]="item.index === activeIndex()"
            [class.selected]="item.icon.id === selected?.id"
            (mousedown)="$event.preventDefault(); pick(item.icon)"
          ><i class="bi" [ngClass]="'bi-' + item.icon.id" aria-hidden="true"></i><span>{{ item.icon.label }}</span><i class="bi bi-check2 fis-check" *ngIf="item.icon.id === selected?.id" aria-hidden="true"></i></button>
        </ng-container>
        <p class="fis-empty" *ngIf="!flat().length">Sin coincidencias. Prueba con otra palabra o elige «General / incluido».</p>
        <p class="fis-more" *ngIf="hiddenCount() > 0">{{ hiddenCount() }} opciones más: escribe para afinar la búsqueda.</p>
      </div>
    </div>
  `,
  styles: [`
    .fis{position:relative;display:block}
    .fis-control{display:flex;align-items:center;gap:.55rem;box-sizing:border-box;width:100%;min-height:48px;padding:0 .5rem 0 .45rem;border:1px solid var(--ts-border-strong);border-radius:9px;background:var(--ts-input-bg);transition:border-color .15s ease,box-shadow .15s ease}
    .fis-control:focus-within{border-color:var(--ts-primary);box-shadow:0 0 0 3px var(--ts-primary-soft-14,rgba(47,124,255,.14))}
    .fis-control.invalid{border-color:var(--ts-danger)}
    .fis-current{flex:none;width:34px;height:34px;display:grid;place-items:center;border-radius:8px;color:var(--ts-text-muted);background:var(--ts-surface);font-size:1.05rem}
    .fis-control.has-value .fis-current{color:var(--ts-text-blue-250-3);background:var(--ts-bg-blue-400-a16)}
    .fis-control input{flex:1;min-width:0;height:46px;margin:0;padding:0;border:0 !important;outline:0 !important;box-shadow:none !important;background:transparent !important;color:var(--ts-text);font:inherit;font-size:.9rem}
    .fis-control.has-value input::placeholder{color:var(--ts-text);opacity:1;font-weight:650}
    .fis-control input::placeholder{color:var(--ts-placeholder,#71849e)}
    .fis-clear{border:0;background:transparent;color:var(--ts-text-muted);cursor:pointer;padding:.3rem}
    .fis-panel{position:absolute;z-index:40;top:calc(100% + 6px);left:0;right:0;max-height:300px;overflow-y:auto;box-sizing:border-box;padding:.35rem;border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-bg-elevated);box-shadow:var(--ts-shadow-md,0 14px 36px rgba(0,7,22,.3))}
    .fis-group{margin:.45rem .55rem .2rem;color:var(--ts-text-muted);font-size:.66rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase}
    .fis-option{display:flex;align-items:center;gap:.65rem;width:100%;box-sizing:border-box;padding:.5rem .6rem;border:0;border-radius:8px;background:transparent;color:var(--ts-text);font:inherit;font-size:.86rem;text-align:left;cursor:pointer}
    .fis-option>i:first-child{width:22px;color:var(--ts-text-blue-250-3);font-size:1.05rem;text-align:center}
    .fis-option span{flex:1}
    .fis-option:hover,.fis-option.active{background:var(--ts-primary-soft-14,rgba(47,124,255,.14))}
    .fis-option.selected{font-weight:750}
    .fis-check{color:var(--ts-success,#3fd08f)}
    .fis-empty,.fis-more{margin:0;padding:.5rem .7rem;color:var(--ts-text-muted);font-size:.78rem}
  `],
})
export class FeatureIconSelectComponent implements OnChanges {
  @Input() icons: SpaceFeatureIcon[] = [];
  @Input() value = '';
  @Input() invalid = false;
  @Input() inputId = 'feature-icon-select';
  /** Máximo de opciones visibles a la vez (el resto se alcanza buscando). */
  @Input() limit = 40;
  @Output() valueChange = new EventEmitter<SpaceFeatureIcon>();

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  searchText = '';
  isOpen = signal(false);
  activeIndex = signal(-1);
  selected: SpaceFeatureIcon | null = null;

  // Resultados cacheados (nunca arreglos nuevos en cada ciclo de detección).
  private flatCache: SpaceFeatureIcon[] = [];
  private groupsCache: { name: string; items: { icon: SpaceFeatureIcon; index: number }[] }[] = [];
  private hidden = 0;

  ngOnChanges(): void {
    this.selected = this.icons.find(icon => icon.id === this.value) ?? null;
    this.recompute();
  }

  flat(): SpaceFeatureIcon[] { return this.flatCache; }
  groups() { return this.groupsCache; }
  hiddenCount(): number { return this.hidden; }

  private recompute(): void {
    const term = normalize(this.searchText);
    const matches = term
      ? this.icons.filter(icon => normalize(`${icon.label} ${icon.keywords} ${icon.group}`).includes(term))
      : this.icons;
    this.flatCache = matches.slice(0, this.limit);
    this.hidden = matches.length - this.flatCache.length;
    const groups = new Map<string, { icon: SpaceFeatureIcon; index: number }[]>();
    this.flatCache.forEach((icon, index) => {
      if (!groups.has(icon.group)) groups.set(icon.group, []);
      groups.get(icon.group)!.push({ icon, index });
    });
    this.groupsCache = [...groups.entries()].map(([name, items]) => ({ name, items }));
  }

  open(): void {
    this.isOpen.set(true);
    this.activeIndex.set(-1);
  }

  focusSearch(): void { this.searchInputRef?.nativeElement.focus(); }

  onType(): void {
    this.recompute();
    this.isOpen.set(true);
    this.activeIndex.set(this.flatCache.length ? 0 : -1);
  }

  onBlur(): void {
    this.isOpen.set(false);
    if (this.searchText) { this.searchText = ''; this.recompute(); }
  }

  pick(icon: SpaceFeatureIcon): void {
    this.selected = icon;
    this.valueChange.emit(icon);
    this.searchText = '';
    this.recompute();
    this.isOpen.set(false);
  }

  onKeydown(evt: KeyboardEvent): void {
    const options = this.flatCache;
    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      if (!this.isOpen()) this.open();
      this.activeIndex.set(Math.min(this.activeIndex() + 1, options.length - 1));
    } else if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeIndex.set(Math.max(this.activeIndex() - 1, 0));
    } else if (evt.key === 'Enter') {
      // Nunca deja que Enter envíe un formulario que lo contenga.
      evt.preventDefault();
      if (!this.isOpen()) return;
      const option = options[this.activeIndex()];
      if (option) this.pick(option);
    } else if (evt.key === 'Escape') {
      if (this.isOpen()) { evt.stopPropagation(); this.isOpen.set(false); }
    }
  }

  trackGroup(_i: number, group: { name: string }): string { return group.name; }
  trackItem(_i: number, item: { icon: SpaceFeatureIcon }): string { return item.icon.id; }
}
